import { useEffect, useRef, useState } from "react";
import { API, api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { MessagesSquare, Send, Loader2, Leaf, Plus } from "lucide-react";

function renderContent(text) {
  return (text || "").split("\n").map((ln, i) => {
    if (ln.startsWith("### ")) return <h4 key={i} className="font-display font-semibold mt-2">{ln.slice(4)}</h4>;
    if (ln.startsWith("## ")) return <h3 key={i} className="font-display font-semibold text-base mt-2">{ln.slice(3)}</h3>;
    if (/^[-*] /.test(ln)) return <li key={i} className="ml-5 list-disc">{ln.slice(2)}</li>;
    if (ln.trim() === "") return <div key={i} className="h-1.5" />;
    return <p key={i}>{ln}</p>;
  });
}

export default function Assistant() {
  const { t, lang } = useLang();
  const SUGGESTIONS = [t("asst.s1"), t("asst.s2"), t("asst.s3"), t("asst.s4")];
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);

  const loadSessions = () => api.get("/chat/sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const openSession = async (id) => {
    setSessionId(id);
    const { data } = await api.get(`/chat/sessions/${id}/messages`);
    setMessages(data.map((m) => ({ role: m.role, content: m.content })));
  };

  const newChat = () => { setSessionId(null); setMessages([]); };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg, session_id: sessionId, lang }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let sid = sessionId;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const data = JSON.parse(line);
            if (data.session_id) { sid = data.session_id; setSessionId(data.session_id); }
            if (data.delta) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
                return copy;
              });
            }
          } catch {}
        }
      }
      loadSessions();
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, something went wrong. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div data-testid="assistant-page" className="flex flex-col lg:flex-row gap-5 h-[calc(100vh-7rem)]">
      <aside className="hidden lg:flex flex-col w-60 shrink-0 bg-white border border-[#DCE3DD] rounded-sm">
        <button data-testid="new-chat-button" onClick={newChat} className="m-3 inline-flex items-center justify-center gap-2 bg-[#275C3F] hover:bg-[#1E4731] text-white py-2 rounded-sm text-sm font-semibold">
          <Plus className="h-4 w-4" /> {t("asst.newChat")}
        </button>
        <div className="flex-1 overflow-y-auto es-scroll px-2 pb-2 space-y-1">
          {sessions.map((s) => (
            <button key={s.id} data-testid={`chat-session-${s.id}`} onClick={() => openSession(s.id)} className={`w-full text-left px-3 py-2 rounded-sm text-sm truncate transition-colors ${sessionId === s.id ? "bg-[#EAF0EA] text-[#275C3F]" : "text-[#4B5950] hover:bg-[#F8FAF8]"}`}>
              {s.title}
            </button>
          ))}
        </div>
      </aside>

      <div className="flex-1 flex flex-col bg-white border border-[#DCE3DD] rounded-sm min-w-0">
        <div className="px-5 py-3 border-b border-[#DCE3DD] flex items-center gap-2">
          <MessagesSquare className="h-5 w-5 text-[#275C3F]" />
          <h1 className="font-display font-semibold">{t("asst.title")}</h1>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto es-scroll p-5 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="h-12 w-12 grid place-items-center bg-[#EAF0EA] rounded-sm"><Leaf className="h-6 w-6 text-[#275C3F]" /></div>
              <h2 className="font-display text-xl font-semibold mt-4">{t("asst.askTitle")}</h2>
              <p className="text-[#4B5950] mt-1 max-w-md">{t("asst.askSub")}</p>
              <div className="grid sm:grid-cols-2 gap-2 mt-6 max-w-xl">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} data-testid={`suggestion-${i}`} onClick={() => send(s)} className="text-left text-sm border border-[#DCE3DD] rounded-sm px-3 py-2 hover:bg-[#EAF0EA] transition-all duration-200">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`message-${m.role}-${i}`} className={`max-w-[85%] rounded-sm px-4 py-2.5 text-sm ${m.role === "user" ? "bg-[#275C3F] text-white" : "bg-[#F8FAF8] border border-[#DCE3DD] text-[#121A14]"}`}>
                  {m.role === "assistant" && m.content === "" && streaming ? (
                    <Loader2 className="h-4 w-4 animate-spin text-[#275C3F]" />
                  ) : (
                    <div className="space-y-0.5">{renderContent(m.content)}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-[#DCE3DD] p-3">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("asst.placeholder")}
              className="flex-1 bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#275C3F] focus:ring-offset-1 outline-none"
            />
            <button data-testid="chat-send-button" type="submit" disabled={streaming || !input.trim()} className="bg-[#275C3F] hover:bg-[#1E4731] text-white p-2.5 rounded-sm disabled:opacity-60 transition-all duration-200 active:scale-[0.98]">
              {streaming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
